import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import {
  CustomIconButton,
  CustomPagination,
  CustomTable,
  CustomTableBody,
  CustomTableRow,
  CustomTypography,
} from '@qler-view/shared_lib/src';
import CustomTableHead from '@qler-view/shared_lib/src/shared/lib/ui-components/Table/CustomTableHead';
import CustomTableCell from '@qler-view/shared_lib/src/shared/lib/ui-components/Table/CustomTableCell';
import ViewIcon from '@assets/images/contactEye.svg';
import EditIcon from '@assets/images/contactEditPencil.svg';
import TrashIcon from '@assets/images/contactTrash.svg';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';
import AddNewContactFacility from './AddNewContactFacility';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import {
  useDeleteContact,
  useGetContactUsAllData,
} from '../../API/hooks/ContactUsHook';
import { type ContactUsAllResponse } from '../../API/services/ContactUsService';
import ConfirmationPopup from '../components/ConfirmationPopup';
import ViewContactFacility from './ViewContactFacility';
import { rowsPerPage } from '../constants';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';

export interface FacilityContactListProps {
  facilityId: string;
}

const FacilityContactList: React.FC<FacilityContactListProps> = ({
  facilityId,
}) => {
  const userId = localStorage.getItem('userId') ?? '0';

  const [open, setOpen] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');
  const [currentFacilityId, setCurrentFacilityId] = useState<string>('');

  const [currentContactId, setCurrentContactId] = useState<string>('');
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [mode, setMode] = useState<string>('');
  const [viewOpen, setViewOpen] = useState(false);
  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );
  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  useEffect(() => {
    setCurrentFacilityId(facilityId);
  }, [facilityId]);

  const { CategoryAllData, refetch } = useGetContactUsAllData({
    userId: userId,
    facilityId: currentFacilityId,
    page,
    size: rowsPerPage,
    sortField,
    sortType,
  });

  const { mutate: DeleteContact } = useDeleteContact(() => onSuccessTost());

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1);
  };

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const onSuccessTost = () => {
    setMessage('Contact deleted successfully!');
    setOpenToast(true);
    setTimeout(() => {
      setOpenToast(false);
    }, 3000);
    refetch();
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      DeleteContact({
        userId: userId,
        contactId: Number(contactId) || 0,
      });
      refetch();
    } catch (error) {
      console.error('Error Deleting Contact:', error);
    }
  };

  return (
    <Box>
      <Box>
        <ConfirmationPopup
          openPopup={confirmationPopup}
          onClosePopup={() => {
            setConfirmationPopup(false);
          }}
          title={'Confirmation Action'}
          message={
            ' Are you sure you want to delete this contact? This action is irreversible.'
          }
          onConfirm={() => {
            setConfirmationPopup(false);
            handleDeleteContact(currentContactId);
          }}
        />
      </Box>
      {openToast && (
        <CustomSnackbar
          id="toast-facility-list"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}
      {mode !== 'view' && (
        <AddNewContactFacility
          open={open}
          onClose={() => {
            setCurrentContactId('');
            setOpen(false);
            refetch();
          }}
          facilityId={facilityId}
          contactId={currentContactId}
          mode={mode}
        />
      )}
      {mode === 'view' && (
        <ViewContactFacility
          open={viewOpen}
          onClose={() => {
            setCurrentContactId('');
            setViewOpen(false);
            refetch();
          }}
          contactId={currentContactId}
        />
      )}
      <Box className="pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className=" flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomTypography variant="h5" id="contact-directory">
                  Contact Directory
                </CustomTypography>
              </Box>
              <Box>
                <CustomButton
                  id="add-new-contact"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setMode('add');
                    setCurrentContactId('');
                    setOpen(true);
                  }}
                >
                  Add Contact
                </CustomButton>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pb-20">
        <CustomTable id="table-contact">
          <CustomTableHead id="table-header-contact">
            <CustomTableRow id="table-header-table-row">
              <CustomTableCell
                id="table-cell-contact-name"
                onClick={() => handleSort('name')}
              >
                NAME &nbsp;
                {sortField === 'name' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-contact-title"
                onClick={() => handleSort('title')}
              >
                TITLE&nbsp;
                {sortField === 'title' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-contact-department"
                onClick={() => handleSort('department')}
              >
                DEPARTMENT&nbsp;
                {sortField === 'department' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-contact-email"
                onClick={() => handleSort('primaryEmail')}
              >
                PRIMARY EMAIL&nbsp;
                {sortField === 'primaryEmail' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-contact-primary-phone"
                onClick={() => handleSort('primaryPhone')}
              >
                PRIMARY PHONE&nbsp;
                {sortField === 'primaryPhone' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell
                id="table-cell-contact-category"
                onClick={() => handleSort('category')}
              >
                CATEGORY&nbsp;
                {sortField === 'category' && getSortIcon(sortType)}
              </CustomTableCell>
              <CustomTableCell id="table-cell-contact-actions">
                ACTIONS
              </CustomTableCell>
            </CustomTableRow>
          </CustomTableHead>
          {CategoryAllData && CategoryAllData?.contactsList.length > 0 ? (
            <CustomTableBody id="table-body-contact">
              {CategoryAllData.contactsList.map(
                (contact: ContactUsAllResponse) => (
                  <CustomTableRow
                    key={contact.id}
                    id={`table-row-contact-${contact.id}`}
                  >
                    <CustomTableCell
                      id={`table-cell-contact-name-${contact.id}`}
                    >
                      {`${contact.name}`}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-contact-title-${contact.id}`}
                    >
                      {contact.title || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-contact-department-${contact.id}`}
                    >
                      {contact.department || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-contact-primary-email-${contact.id}`}
                    >
                      {contact.primaryEmail || 'N/A'}
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-contact-primary-phone-${contact.id}`}
                    >
                      {contact.primaryPhone || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-contact-category-${contact.id}`}
                    >
                      {contact.category || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell id="table-cell-contact-actions">
                      <Box className="flex align-items-center">
                        <Box className="contact-view-box">
                          <CustomIconButton
                            id="view-icon"
                            size="small"
                            onClick={() => {
                              setMode('view');
                              setCurrentContactId(contact.id?.toString() ?? '');
                              setViewOpen(true);
                            }}
                            icon={<ViewIcon />}
                          />
                        </Box>
                        <Box className="ml-8">
                          <CustomIconButton
                            id="edit-icon"
                            size="small"
                            onClick={() => {
                              setMode('edit');
                              setCurrentContactId(contact.id?.toString() ?? '');
                              setOpen(true);
                            }}
                            icon={<EditIcon />}
                          />
                        </Box>
                        <Box className="ml-8">
                          <CustomIconButton
                            id="trash-icon"
                            size="small"
                            onClick={() => {
                              setCurrentContactId(contact.id?.toString() ?? '');
                              setConfirmationPopup(true);
                            }}
                            icon={<TrashIcon />}
                          />
                        </Box>
                      </Box>
                    </CustomTableCell>
                  </CustomTableRow>
                )
              )}
            </CustomTableBody>
          ) : (
            <CustomTableBody id="table-body-contact">
              <CustomTableRow id="table-row-contact">
                <CustomTableCell id="table-cell-contact-id" colspan={7}>
                  <Box className="flex flex-justify-center align-items-center">
                    <CustomTypography
                      id="no-contacts-found"
                      variant="body2"
                      color="textDisabled"
                    >
                      No Contacts Found
                    </CustomTypography>
                  </Box>
                </CustomTableCell>
              </CustomTableRow>
            </CustomTableBody>
          )}
        </CustomTable>
        {CategoryAllData && CategoryAllData.totalRecords > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(
                      page,
                      rowsPerPage,
                      CategoryAllData?.totalRecords
                    )}{' '}
                    of {CategoryAllData?.totalRecords} Facilities
                  </CustomTypography>
                </Box>
              </Grid2>
              {CategoryAllData?.totalRecords > rowsPerPage && (
                <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                  <Box display="flex" justifyContent="end">
                    <CustomPagination
                      id="facility-list-pagination"
                      count={Math.ceil(
                        CategoryAllData?.totalRecords / rowsPerPage
                      )}
                      page={page + 1}
                      onPageChange={(_, value) => handlePageChange(value - 1)}
                      color="standard"
                    />
                  </Box>
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FacilityContactList;
